/**
 * RevenueCat Subscription Service
 * ================================
 * Handles in-app purchases via RevenueCat for iOS and Android.
 * €9.99/month "premium" subscription.
 *
 * On web/preview, this module provides a mock that delegates
 * to the existing backend subscription endpoints so the paywall
 * still works during development.
 */
import { Capacitor } from '@capacitor/core';

// RevenueCat SDK keys — set in environment or hardcode after RevenueCat dashboard setup
const RC_IOS_KEY = process.env.REACT_APP_RC_IOS_KEY || 'test_yNLBYisqgnzucXmZzRdOviotjke';
const RC_ANDROID_KEY = process.env.REACT_APP_RC_ANDROID_KEY || 'test_yNLBYisqgnzucXmZzRdOviotjke';

const ENTITLEMENT_ID = 'premium';
const PRODUCT_ID = 'bodymax_premium_monthly';

let purchasesModule = null;

/**
 * Check if we're running in a native Capacitor shell (not web browser).
 */
export function isNativePlatform() {
  return Capacitor.isNativePlatform();
}

/**
 * Initialize RevenueCat. Call once on app startup.
 * On web, this is a no-op.
 */
export async function initRevenueCat(appUserId) {
  if (!isNativePlatform()) return;

  try {
    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    purchasesModule = Purchases;

    const platform = Capacitor.getPlatform();
    const apiKey = platform === 'ios' ? RC_IOS_KEY : RC_ANDROID_KEY;

    if (!apiKey) {
      console.warn('[RevenueCat] No API key configured for platform:', platform);
      return;
    }

    await Purchases.configure({
      apiKey,
      appUserID: appUserId || undefined,
    });

    console.log('[RevenueCat] Initialized for', platform);
  } catch (err) {
    console.error('[RevenueCat] Init failed:', err);
  }
}

/**
 * Identify the user after login (links RevenueCat subscriber to your user ID).
 */
export async function identifyUser(userId) {
  if (!purchasesModule) return;
  try {
    await purchasesModule.logIn({ appUserID: userId });
  } catch (err) {
    console.error('[RevenueCat] Identify failed:', err);
  }
}

/**
 * Log out (resets RevenueCat to anonymous user).
 */
export async function logOutRevenueCat() {
  if (!purchasesModule) return;
  try {
    await purchasesModule.logOut();
  } catch (err) {
    console.error('[RevenueCat] Logout failed:', err);
  }
}

/**
 * Get available subscription offerings.
 * Returns { monthly: package } or null.
 */
export async function getOfferings() {
  if (!purchasesModule) return null;
  try {
    const { offerings } = await purchasesModule.getOfferings();
    if (offerings?.current) {
      return {
        monthly: offerings.current.monthly || offerings.current.availablePackages?.[0],
        allPackages: offerings.current.availablePackages || [],
      };
    }
    return null;
  } catch (err) {
    console.error('[RevenueCat] getOfferings failed:', err);
    return null;
  }
}

/**
 * Purchase the monthly premium subscription.
 * Returns { success: boolean, customerInfo? }
 */
export async function purchaseMonthly() {
  if (!purchasesModule) return { success: false, error: 'Not initialized' };
  try {
    const offerings = await getOfferings();
    if (!offerings?.monthly) {
      return { success: false, error: 'No monthly package available' };
    }

    const { customerInfo } = await purchasesModule.purchasePackage({
      aPackage: offerings.monthly,
    });

    const isPremium = customerInfo?.entitlements?.active?.[ENTITLEMENT_ID]?.isActive;
    return { success: !!isPremium, customerInfo };
  } catch (err) {
    // User cancelled or billing error
    if (err?.code === 1 || err?.userCancelled) {
      return { success: false, error: 'cancelled' };
    }
    console.error('[RevenueCat] Purchase failed:', err);
    return { success: false, error: err?.message || 'Purchase failed' };
  }
}

/**
 * Check if the user currently has an active premium entitlement.
 */
export async function checkPremiumStatus() {
  if (!purchasesModule) return { isPremium: false };
  try {
    const { customerInfo } = await purchasesModule.getCustomerInfo();
    const premium = customerInfo?.entitlements?.active?.[ENTITLEMENT_ID];
    return {
      isPremium: !!premium?.isActive,
      expiresAt: premium?.expirationDate || null,
      productId: premium?.productIdentifier || null,
      willRenew: premium?.willRenew ?? false,
    };
  } catch (err) {
    console.error('[RevenueCat] Status check failed:', err);
    return { isPremium: false };
  }
}

/**
 * Restore purchases (e.g., after reinstall or new device).
 */
export async function restorePurchases() {
  if (!purchasesModule) return { success: false };
  try {
    const { customerInfo } = await purchasesModule.restorePurchases();
    const isPremium = customerInfo?.entitlements?.active?.[ENTITLEMENT_ID]?.isActive;
    return { success: true, isPremium: !!isPremium };
  } catch (err) {
    console.error('[RevenueCat] Restore failed:', err);
    return { success: false, error: err?.message };
  }
}
